import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

export interface AuditAPI {
  createCrossLog: (
    appId: string,
    resourceType: string,
    resourceId: string,
    action: string,
    description: string
  ) => Promise<LogEntry>
  createLog: (resourceType: string, resourceId: string, action: string, description: string) => Promise<LogEntry>
  getAllLogs: (resourceType: string, resourceId: string, page?: number, perPage?: number) => Promise<GetLogs>
}

interface GetLogs {
  audit_logs: LogEntry[]
  pagination: Pagination
}

interface Pagination {
  page: number
  pages: number
  per_page: number
  total: number
}

export interface AuditResourceAPI {
  createLog: (action: string, description: string) => Promise<LogEntry>
  getAllLogs: (page?: number, perPage?: number) => Promise<GetLogs>
}

export interface LogEntry {
  id: number
  app: string
  resource: string
  resource_id: string
  action: string
  description: string
  agent: string
  inserted_at: string
  updated_at: string
}

const AuditCtx = createContext<AuditAPI | null>(null)
const AuditResourceCtx = createContext<AuditResourceAPI | null>(null)

export function AuditService(
  props: PropsWithChildren<{ auth_token: string; svc: string; appId: string; agent: string }>
): JSX.Element {
  const createCrossLog = (
    appId: string,
    resourceType: string,
    resourceId: string,
    action: string,
    description: string
  ): Promise<LogEntry> => {
    const payload = {
      audit_log: {
        app: appId,
        resource: resourceType,
        resource_id: resourceId,
        action: action,
        description: description,
        agent: props.agent,
      },
    }
    return fetch(props.svc, {
      method: 'POST',
      headers: authHeaders(props.auth_token),
      body: JSON.stringify(payload),
    }).then((res) => res.json())
  }
  const createLog = (
    resourceType: string,
    resourceId: string,
    action: string,
    description: string
  ): Promise<LogEntry> => createCrossLog(props.appId, resourceType, resourceId, action, description)

  const getAllLogs = (resourceType: string, resourceId: string, page = 1, perPage = 20): Promise<GetLogs> => {
    const query = `?page=${page}&per_page=${perPage}&app=${encodeURIComponent(
      props.appId
    )}&resource=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}`
    return fetch(props.svc + query, {
      method: 'GET',
      headers: authHeaders(props.auth_token),
    }).then((res) => res.json())
  }

  const api = {
    createCrossLog,
    createLog,
    getAllLogs,
  }
  return <AuditCtx.Provider value={api}>{props.children}</AuditCtx.Provider>
}

export function AuditResource(props: PropsWithChildren<{ resourceType: string; resourceId: string }>): JSX.Element {
  const auditAPI = useAuditService()
  const createLog = (action: string, description: string): Promise<LogEntry> => {
    return auditAPI.createLog(props.resourceType, props.resourceId, action, description)
  }
  const getAllLogs = (page = 1, perPage = 20): Promise<GetLogs> => {
    return auditAPI.getAllLogs(props.resourceType, props.resourceId, page, perPage)
  }
  const api = {
    createLog,
    getAllLogs,
  }
  return <AuditResourceCtx.Provider value={api}>{props.children}</AuditResourceCtx.Provider>
}

export function useAuditService(): AuditAPI {
  const auditAPI = useContext(AuditCtx)
  return auditAPI as AuditAPI
}

export function useAuditResourceService(): AuditResourceAPI {
  const auditAPI = useContext(AuditResourceCtx)
  return auditAPI as AuditResourceAPI
}

export function useAuditLogs(limit = 20) {
  const resourceApi = useAuditResourceService()
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [error, setError] = useState<any | null>(null)
  const [page, setPage] = useState<number>(1)
  const [pagination, setPagination] = useState<Pagination>({ page: 1, pages: 0, per_page: 0, total: 0 })

  useEffect(() => {
    resourceApi
      .getAllLogs(page, limit)
      .then((res: GetLogs) => {
        setLogs([...logs, ...res.audit_logs])
        setPagination(res.pagination)
      })
      .catch(setError)
  }, [page])

  const loadMore = () => {
    if (pagination.pages > page) {
      setPage(pagination.page + 1)
    }
  }

  const nextPage = () => {
    if (pagination.pages > pagination.page) {
      resourceApi
        .getAllLogs(pagination.page + 1, limit)
        .then((res: GetLogs) => {
          setLogs(res.audit_logs)
          setPagination(res.pagination)
        })
        .catch(setError)
    }
  }

  const prevPage = () => {
    if (pagination.page > 1) {
      resourceApi
        .getAllLogs(pagination.page - 1, limit)
        .then((res: GetLogs) => {
          setLogs(res.audit_logs)
          setPagination(res.pagination)
        })
        .catch(setError)
    }
  }

  return { logs, error, loadMore, pagination, nextPage, prevPage }
}

const authHeaders = (token: string) => {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}
